import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { deductBalls } from '../services/BallsService';
import { EQUIPMENT_CATALOG, RARITY_COLORS, getEquipmentByType } from '../config/equipmentConfig';
import type { EquipmentItem } from '../config/equipmentConfig';
import './EquipmentPage.css';

interface EquipmentPageProps {
  onNavigate?: (page: string) => void;
  userData?: any;
}

const RARITY_ORDER: Record<string, number> = { common: 0, rare: 1, epic: 2 };
const RARITY_GLOW: Record<string, string> = {
  common: '0 0 12px rgba(156,163,175,0.15)',
  rare: '0 0 16px rgba(59,130,246,0.25)',
  epic: '0 0 20px rgba(245,158,11,0.35)',
};
const RARITY_BORDER: Record<string, string> = {
  common: 'rgba(156,163,175,0.2)',
  rare: 'rgba(59,130,246,0.3)',
  epic: 'rgba(245,158,11,0.4)',
};
const TYPE_ICON: Record<string, string> = { hat: '🧢', glove: '🧤', sunglasses: '🕶️' };
const TYPE_LABEL: Record<string, string> = { hat: 'Hat', glove: 'Batting Gloves', sunglasses: 'Sunglasses' };
const TYPE_BG: Record<string, string> = {
  hat: 'linear-gradient(135deg, #1a1209 0%, #0d1a2e 100%)',
  glove: 'linear-gradient(135deg, #091a12 0%, #0d1a2e 100%)',
  sunglasses: 'linear-gradient(135deg, #1a0915 0%, #0d1a2e 100%)',
};

const EquipmentPage: React.FC<EquipmentPageProps> = ({ userData }) => {
  const [selectedType, setSelectedType] = useState<'all' | 'hat' | 'glove' | 'sunglasses'>('all');
  const [ballsBalance, setBallsBalance] = useState(0);
  const [ownedEquipment, setOwnedEquipment] = useState<string[]>([]);
  const [buying, setBuying] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadBalance();
    loadOwnedEquipment();
  }, [userData?.id]);

  async function loadBalance() {
    if (!userData?.id || !isSupabaseConfigured() || !supabase) return;
    const { data } = await supabase.from('users').select('balls_balance').eq('id', userData.id).single();
    if (data) setBallsBalance(data.balls_balance || 0);
  }

  async function loadOwnedEquipment() {
    if (!userData?.id || !isSupabaseConfigured() || !supabase) return;
    // Load equipment from players owned by this user's teams
    const { data: myTeams } = await supabase.from('teams').select('id').eq('owner_id', userData.id);
    if (!myTeams?.length) return;
    const teamIds = myTeams.map(t => t.id);
    const { data: players } = await supabase.from('players').select('equipment').in('team_id', teamIds);
    const owned = new Set<string>();
    (players || []).forEach(p => {
      if (p.equipment && typeof p.equipment === 'object') {
        Object.values(p.equipment).forEach((slot: any) => {
          if (slot?.itemId) owned.add(slot.itemId);
        });
      }
    });
    setOwnedEquipment(Array.from(owned));
  }

  async function handleBuy(item: EquipmentItem) {
    if (!userData?.id || !isSupabaseConfigured()) {
      setMessage({ text: 'Sign in to purchase equipment.', type: 'error' });
      return;
    }
    if (buying) return;
    setBuying(item.id);

    try {
      const result = await deductBalls(
        userData.id,
        item.cost,
        'equipment_purchase',
        `Equipment purchase: ${item.name}`,
        item.id,
        'equipment'
      );

      if (!result.success) {
        setMessage({ text: result.error || 'Insufficient balance', type: 'error' });
        setBuying(null);
        return;
      }

      setBallsBalance(result.balance || 0);
      setMessage({ text: `✅ ${item.name} purchased for ${item.cost.toLocaleString()} $BALLS!`, type: 'success' });
    } catch (err) {
      setMessage({ text: 'Purchase failed. Try again.', type: 'error' });
    } finally {
      setBuying(null);
      setTimeout(() => setMessage(null), 4000);
    }
  }

  const equipment = selectedType === 'all'
    ? [...EQUIPMENT_CATALOG].sort((a, b) => RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity])
    : getEquipmentByType(selectedType);

  return (
    <div className="equip-page">
      <div className="equip-header">
        <div>
          <h1>🎒 Equipment Shop</h1>
          <p className="equip-subtitle">Boost your players with attribute-enhancing gear</p>
        </div>
        <div className="equip-balance">
          <span className="equip-balance-label">YOUR BALANCE</span>
          <span className="equip-balance-amount">{ballsBalance.toLocaleString()} $BALLS</span>
        </div>
      </div>

      {/* Message toast */}
      {message && (
        <div className={`equip-toast equip-toast-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Type filter tabs */}
      <div className="equip-tabs">
        {(['all', 'hat', 'glove', 'sunglasses'] as const).map(t => (
          <button
            key={t}
            className={`equip-tab ${selectedType === t ? 'active' : ''}`}
            onClick={() => setSelectedType(t)}
          >
            {t === 'all' ? '⚡ All' : `${TYPE_ICON[t]} ${TYPE_LABEL[t]}`}
          </button>
        ))}
      </div>

      {/* Equipment grid */}
      <div className="equip-grid">
        {equipment.map(item => {
          const canAfford = ballsBalance >= item.cost;
          const isBuying = buying === item.id;
          return (
            <div
              key={item.id}
              className="equip-card"
              style={{
                borderColor: RARITY_BORDER[item.rarity],
                boxShadow: RARITY_GLOW[item.rarity],
                background: TYPE_BG[item.type],
              }}
            >
              <div className="equip-card-rarity" style={{ color: RARITY_COLORS[item.rarity] }}>
                {item.rarity.toUpperCase()}
              </div>
              <div className="equip-card-icon">{TYPE_ICON[item.type]}</div>
              <div className="equip-card-name">{item.name}</div>
              <div className="equip-card-desc">{item.description}</div>
              <div className="equip-card-boost">
                <span className="equip-boost-value" style={{ color: RARITY_COLORS[item.rarity] }}>
                  +{item.boost}
                </span>
                <span className="equip-boost-label">ATTR</span>
              </div>
              <button
                className={`equip-buy-btn ${!canAfford ? 'disabled' : ''} ${item.rarity}`}
                disabled={!canAfford || !!buying}
                onClick={() => handleBuy(item)}
              >
                {isBuying ? 'Processing...' : `${item.cost.toLocaleString()} ⚾`}
              </button>
            </div>
          );
        })}
      </div>

      {/* How it works */}
      <div className="equip-info">
        <h3>💡 How Equipment Works</h3>
        <div className="equip-info-grid">
          <div className="equip-info-step">
            <span className="equip-info-num">1</span>
            <span>Purchase equipment from this shop</span>
          </div>
          <div className="equip-info-step">
            <span className="equip-info-num">2</span>
            <span>Go to your player's profile</span>
          </div>
          <div className="equip-info-step">
            <span className="equip-info-num">3</span>
            <span>Equip it in the Hat, Batting Gloves, or Sunglasses slot</span>
          </div>
          <div className="equip-info-step">
            <span className="equip-info-num">4</span>
            <span>Upgrade anytime — pay only the difference!</span>
          </div>
        </div>
        <p className="equip-info-note">Equipment stays with the player, even on the marketplace. Upgrading refunds the old item's value and charges the difference.</p>
      </div>
    </div>
  );
};

export default EquipmentPage;
